import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Calendar, CheckCircle2, Download, ExternalLink, TrendingUp } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Select,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  textColumn,
  type ColumnDef,
} from '@/components/ui'
import {
  reportCsvUrl,
  useBillingOverview,
  useExpiryReport,
  useMarginReport,
  useMrrReport,
  type BillingOverviewRow,
  type ExpiryBucket,
  type ExpiryRow,
  type MarginRow,
  type MrrByGroup,
} from '../api/reportsApi.ts'
import { PriceAdjustmentsSection } from './PriceAdjustmentsSection.tsx'
import { SlaSection } from './SlaSection.tsx'
import { PlausibilitySection } from './PlausibilitySection.tsx'
import { useAnonymize } from '@/hooks/useAnonymize.ts'

function fmtCents(cents: number, currency = 'EUR'): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency })
}

function downloadCsv(url: string): void {
  window.open(url, '_blank', 'noopener')
}

// ─── Sub: Billing Overview ─────────────────────────────────────────────────

function BillingOverviewReport() {
  const navigate = useNavigate()
  const a = useAnonymize()
  const [year, setYear] = useState(() => new Date().getUTCFullYear())
  const { data, isLoading, error, refetch } = useBillingOverview(year)

  const yearOptions = [year - 2, year - 1, year, year + 1].map((y) => ({ value: String(y), label: String(y) }))

  if (isLoading) return <LoadingState label="Lade Abrechnungsübersicht …" />
  if (error) {
    return (
      <ErrorState
        description="Bericht konnte nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }
  if (!data) return <EmptyState title="Keine Daten" />

  const columns: ColumnDef<BillingOverviewRow, unknown>[] = [
    {
      accessorKey: 'contractNumber',
      header: 'Vertrag',
      size: 130,
      cell: ({ row }) => (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-brand-700 hover:underline"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/vertraege/${row.original.contractId}`)
          }}
        >
          <ExternalLink className="w-3 h-3" />
          {row.original.contractNumber}
        </button>
      ),
    },
    {
      accessorKey: 'customerName',
      header: 'Kunde',
      cell: ({ row }) => <span>{a.name(row.original.customerName ?? '—')}</span>,
    },
    textColumn<BillingOverviewRow>('productName', 'Produkt'),
    {
      accessorKey: 'itemsCount',
      header: 'Posten',
      size: 90,
      cell: ({ getValue }) => <span className="tabular-nums">{String(getValue())}</span>,
    },
    {
      accessorKey: 'totalCents',
      header: 'Summe',
      size: 140,
      cell: ({ row }) => (
        <span className="tabular-nums font-medium text-gray-900">
          {fmtCents(row.original.totalCents, row.original.currency)}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Select
            value={String(year)}
            onValueChange={(v) => setYear(Number(v))}
            options={yearOptions}
            wrapperClassName="w-40"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Download className="w-4 h-4" />}
          onClick={() => downloadCsv(reportCsvUrl('billing-overview', { year }))}
        >
          CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <Card.Body>
            <p className="text-caption text-gray-500">Gesamt fakturiert</p>
            <p className="text-h3 text-gray-900">{fmtCents(data.totalCents, data.currency)}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <p className="text-caption text-gray-500">Verträge</p>
            <p className="text-h3 text-gray-900">{data.contractsCount}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <p className="text-caption text-gray-500">Posten</p>
            <p className="text-h3 text-gray-900">{data.itemsCount}</p>
          </Card.Body>
        </Card>
      </div>

      <DataTable<BillingOverviewRow>
        data={data.rows}
        columns={columns}
        getRowId={(r) => r.contractId}
        searchPlaceholder="Verträge suchen …"
        emptyState={<EmptyState title="Noch keine Abrechnung in dieser Periode" />}
      />
    </div>
  )
}

// ─── Sub: MRR / ARR ────────────────────────────────────────────────────────

function MrrReport() {
  const { data, isLoading, error, refetch } = useMrrReport()

  if (isLoading) return <LoadingState label="Berechne MRR …" />
  if (error) {
    return (
      <ErrorState
        description="MRR-Bericht konnte nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }
  if (!data) return <EmptyState title="Keine Daten" />

  const groupColumns: ColumnDef<MrrByGroup, unknown>[] = [
    textColumn<MrrByGroup>('groupLabel', 'Bezeichnung'),
    {
      accessorKey: 'mrrCents',
      header: 'MRR',
      size: 150,
      cell: ({ row }) => (
        <span className="tabular-nums font-medium text-gray-900">
          {fmtCents(row.original.mrrCents, data.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'contractsCount',
      header: 'Verträge',
      size: 110,
      cell: ({ getValue }) => <span className="tabular-nums">{String(getValue())}</span>,
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Download className="w-4 h-4" />}
          onClick={() => downloadCsv(reportCsvUrl('mrr'))}
        >
          CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <Card.Body>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <p className="text-caption text-gray-500">MRR (Monthly Recurring Revenue)</p>
            </div>
            <p className="text-h3 text-gray-900">{fmtCents(data.totalMrrCents, data.currency)}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <p className="text-caption text-gray-500">ARR (Annual Recurring Revenue)</p>
            </div>
            <p className="text-h3 text-gray-900">{fmtCents(data.totalArrCents, data.currency)}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <p className="text-caption text-gray-500">Aktive Verträge mit MRR</p>
            <p className="text-h3 text-gray-900">{data.contractsActive}</p>
          </Card.Body>
        </Card>
      </div>

      <Card>
        <Card.Header>
          <Card.Title>MRR pro Produkt</Card.Title>
        </Card.Header>
        <Card.Body>
          <DataTable<MrrByGroup>
            data={data.byProduct}
            columns={groupColumns}
            getRowId={(g) => g.group}
            searchable={false}
            emptyState={<EmptyState title="Keine Produkte mit MRR" />}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title>MRR pro Tarif</Card.Title>
        </Card.Header>
        <Card.Body>
          <DataTable<MrrByGroup>
            data={data.byTariff}
            columns={groupColumns}
            getRowId={(g) => g.group}
            searchable={false}
            emptyState={<EmptyState title="Keine Tarife mit MRR" />}
          />
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Sub: Margen ────────────────────────────────────────────────────────────

function MarginReport() {
  const navigate = useNavigate()
  const a = useAnonymize()
  const { data, isLoading, error, refetch } = useMarginReport()

  if (isLoading) return <LoadingState label="Berechne Margen …" />
  if (error) {
    return (
      <ErrorState
        description="Margen-Bericht konnte nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }
  if (!data) return <EmptyState title="Keine Daten" />

  const columns: ColumnDef<MarginRow, unknown>[] = [
    {
      accessorKey: 'contractNumber',
      header: 'Vertrag',
      size: 130,
      cell: ({ row }) => (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-brand-700 hover:underline"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/vertraege/${row.original.contractId}`)
          }}
        >
          <ExternalLink className="w-3 h-3" />
          {row.original.contractNumber}
        </button>
      ),
    },
    {
      accessorKey: 'customerName',
      header: 'Kunde',
      cell: ({ row }) => <span>{a.name(row.original.customerName ?? '—')}</span>,
    },
    textColumn<MarginRow>('productName', 'Produkt'),
    {
      accessorKey: 'monthlyRevenueCents',
      header: 'Erlös/Monat',
      size: 130,
      cell: ({ row }) => (
        <span className="tabular-nums">{fmtCents(row.original.monthlyRevenueCents, row.original.currency)}</span>
      ),
    },
    {
      accessorKey: 'monthlyCostCents',
      header: 'Kosten/Monat',
      size: 130,
      cell: ({ row }) => (
        <span className="tabular-nums">{fmtCents(row.original.monthlyCostCents, row.original.currency)}</span>
      ),
    },
    {
      accessorKey: 'monthlyMarginCents',
      header: 'Marge/Monat',
      size: 140,
      cell: ({ row }) => (
        <span
          className={`tabular-nums font-medium ${row.original.monthlyMarginCents >= 0 ? 'text-green-700' : 'text-red-700'}`}
        >
          {fmtCents(row.original.monthlyMarginCents, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'marginPercent',
      header: '%',
      size: 90,
      cell: ({ row }) => {
        const p = row.original.marginPercent
        if (p == null) return <span className="text-caption text-gray-400">—</span>
        const variant = p < 10 ? 'danger' : p < 25 ? 'warning' : 'success'
        return <Badge variant={variant} size="sm">{p.toFixed(1)} %</Badge>
      },
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Download className="w-4 h-4" />}
          onClick={() => downloadCsv(reportCsvUrl('margin'))}
        >
          CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <Card.Body>
            <p className="text-caption text-gray-500">Erlös/Monat</p>
            <p className="text-h3 text-gray-900">{fmtCents(data.totalRevenueCents, data.currency)}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <p className="text-caption text-gray-500">Kosten/Monat</p>
            <p className="text-h3 text-gray-900">{fmtCents(data.totalCostCents, data.currency)}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <p className="text-caption text-gray-500">Marge/Monat</p>
            <p className={`text-h3 ${data.totalMarginCents >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {fmtCents(data.totalMarginCents, data.currency)}
            </p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <p className="text-caption text-gray-500">Ø Marge</p>
            <p className="text-h3 text-gray-900">
              {data.averageMarginPercent != null ? `${data.averageMarginPercent.toFixed(1)} %` : '—'}
            </p>
          </Card.Body>
        </Card>
      </div>

      <DataTable<MarginRow>
        data={data.rows}
        columns={columns}
        getRowId={(r) => r.contractId}
        searchPlaceholder="Verträge suchen …"
        emptyState={
          <EmptyState
            title="Keine Margen-Daten"
            description="Margen werden berechnet, sobald Verträge mit Tarif-Komponenten (mit Einkaufspreis) hinterlegt sind."
          />
        }
      />
    </div>
  )
}

// ─── Sub: Fristen / Risiko ─────────────────────────────────────────────────

const BUCKET_LABELS: Record<ExpiryBucket, string> = {
  '0_3': 'in ≤ 3 Monaten',
  '3_6': 'in 3-6 Monaten',
  '6_12': 'in 6-12 Monaten',
  over_12: 'in > 12 Monaten',
  no_end: 'unbefristet',
}

function ExpiryReport() {
  const navigate = useNavigate()
  const a = useAnonymize()
  const { data, isLoading, error, refetch } = useExpiryReport()
  const [selectedBucket, setSelectedBucket] = useState<ExpiryBucket | 'all'>('0_3')

  const filtered = useMemo(() => {
    const rows = data?.rows ?? []
    if (selectedBucket === 'all') return rows
    return rows.filter((r) => r.bucket === selectedBucket)
  }, [data, selectedBucket])

  if (isLoading) return <LoadingState label="Lade Fristen-Bericht …" />
  if (error) {
    return (
      <ErrorState
        description="Bericht konnte nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }
  if (!data) return <EmptyState title="Keine Daten" />

  const columns: ColumnDef<ExpiryRow, unknown>[] = [
    {
      accessorKey: 'contractNumber',
      header: 'Vertrag',
      size: 130,
      cell: ({ row }) => (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-brand-700 hover:underline"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/vertraege/${row.original.contractId}`)
          }}
        >
          <ExternalLink className="w-3 h-3" />
          {row.original.contractNumber}
        </button>
      ),
    },
    {
      accessorKey: 'customerName',
      header: 'Kunde',
      cell: ({ row }) => <span>{a.name(row.original.customerName ?? '—')}</span>,
    },
    {
      accessorKey: 'endDate',
      header: 'Ende',
      size: 120,
      cell: ({ row }) =>
        row.original.endDate ? (
          new Date(row.original.endDate).toLocaleDateString('de-DE')
        ) : (
          <span className="text-caption text-gray-500">unbefristet</span>
        ),
    },
    {
      accessorKey: 'daysUntilEnd',
      header: 'Tage bis Ende',
      size: 130,
      cell: ({ row }) => {
        const d = row.original.daysUntilEnd
        if (d == null) return <span className="text-caption text-gray-400">—</span>
        const variant = d <= 30 ? 'danger' : d <= 90 ? 'warning' : 'neutral'
        return <Badge variant={variant} size="sm">{d} Tage</Badge>
      },
    },
    {
      accessorKey: 'noticePeriodDays',
      header: 'Frist',
      size: 100,
      cell: ({ getValue }) => <span className="tabular-nums">{String(getValue())} T</span>,
    },
    {
      accessorKey: 'autoRenew',
      header: 'Auto-Verl.',
      size: 110,
      cell: ({ getValue }) => <StatusBadge status={getValue() ? 'active' : 'inactive'} />,
    },
  ]

  const buckets: { value: ExpiryBucket | 'all'; label: string; count: number; tone?: 'danger' | 'warning' }[] = [
    { value: '0_3', label: BUCKET_LABELS['0_3'], count: data.bucketCounts['0_3'], tone: 'danger' },
    { value: '3_6', label: BUCKET_LABELS['3_6'], count: data.bucketCounts['3_6'], tone: 'warning' },
    { value: '6_12', label: BUCKET_LABELS['6_12'], count: data.bucketCounts['6_12'] },
    { value: 'over_12', label: BUCKET_LABELS.over_12, count: data.bucketCounts.over_12 },
    { value: 'no_end', label: BUCKET_LABELS.no_end, count: data.bucketCounts.no_end },
    { value: 'all', label: 'Alle', count: data.rows.length },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {buckets.map((b) => (
            <Button
              key={b.value}
              variant={selectedBucket === b.value ? 'primary' : 'ghost'}
              size="sm"
              leadingIcon={
                b.tone === 'danger' ? (
                  <AlertTriangle className="w-4 h-4" />
                ) : b.tone === 'warning' ? (
                  <AlertTriangle className="w-4 h-4" />
                ) : b.value === 'no_end' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <Calendar className="w-4 h-4" />
                )
              }
              onClick={() => setSelectedBucket(b.value)}
            >
              {b.label} ({b.count})
            </Button>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Download className="w-4 h-4" />}
          onClick={() => downloadCsv(reportCsvUrl('expiry'))}
        >
          CSV
        </Button>
      </div>

      <DataTable<ExpiryRow>
        data={filtered}
        columns={columns}
        getRowId={(r) => r.contractId}
        searchPlaceholder="Verträge suchen …"
        emptyState={
          <EmptyState
            title="Keine Verträge in diesem Zeitfenster"
            description="Wechsle das Zeitfenster oben, um Verträge mit anderem Endtermin zu sehen."
          />
        }
      />
    </div>
  )
}

// ─── Top-Level Reporting Section mit Sub-Tabs ──────────────────────────────

export function ReportingSection() {
  return (
    <Tabs defaultValue="billing-overview">
      <TabsList>
        <TabsTrigger value="billing-overview">Abrechnungsübersicht</TabsTrigger>
        <TabsTrigger value="mrr">MRR / ARR</TabsTrigger>
        <TabsTrigger value="margin">Margen</TabsTrigger>
        <TabsTrigger value="expiry">Fristen / Risiko</TabsTrigger>
        <TabsTrigger value="adjustments">Preisanpassungen</TabsTrigger>
        <TabsTrigger value="sla">SLA-Gutschriften</TabsTrigger>
        <TabsTrigger value="plausibility">Plausibilität</TabsTrigger>
      </TabsList>
      <TabsContent value="billing-overview">
        <BillingOverviewReport />
      </TabsContent>
      <TabsContent value="mrr">
        <MrrReport />
      </TabsContent>
      <TabsContent value="margin">
        <MarginReport />
      </TabsContent>
      <TabsContent value="expiry">
        <ExpiryReport />
      </TabsContent>
      <TabsContent value="adjustments">
        <PriceAdjustmentsSection />
      </TabsContent>
      <TabsContent value="sla">
        <SlaSection />
      </TabsContent>
      <TabsContent value="plausibility">
        <PlausibilitySection />
      </TabsContent>
    </Tabs>
  )
}
